import { Box } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { getEventDays } from '.';
import type { ICalendarPlusSettings, IEvent } from '../../../../lib';

const generateEventsResult = (trackEvents, events) => {
	// Create a Set of event IDs for quick lookup
	const eventSet = new Set(events.map((event) => event.id));
	// Map over trackEvents and check if each event ID is in eventSet
	return trackEvents.map((trackEvent) => (eventSet.has(trackEvent.id) ? trackEvent : ''));
};

interface IMultiDayEventLinesProps {
	events: IEvent[];
	trackEvents: IEvent[];
	dayDate: Date;
	eventColor: string;
	settings: ICalendarPlusSettings;
}

export const MultiDayEventLines = ({
	dayDate,
	events,
	eventColor,
	settings,
	trackEvents,
}: IMultiDayEventLinesProps) => {
	// Precompute event info (memoized values) using useMemo
	const eventInfo = useMemo(() => {
		return generateEventsResult(trackEvents, events).map((event) => {
			if (event === '') {
				// Return null for empty events
				return null;
			}

			// Compute the necessary values for each event
			const eventDays = getEventDays(event);
			const isStart = eventDays[0].getTime() === dayDate.getTime();
			const isEnd = eventDays[eventDays.length - 1].getTime() === dayDate.getTime();
			const isInRange = eventDays.some((eventDay) => eventDay.getTime() === dayDate.getTime());
			const lineBGColor =
				event.color || settings.eventCategoryColors[event.eventType?.[0]]?.background || eventColor;

			return { event, isStart, isEnd, isInRange, lineBGColor };
		});
	}, [trackEvents, events, dayDate, settings, eventColor]); // Dependencies for useMemo

	return (
		<React.Fragment>
			{eventInfo.map((info, index) => {
				if (!info) {
					// If event is empty, render a transparent line
					return (
						<Box
							key={index}
							width='full'
							position='relative'
							height='2px'
							marginBottom='2px'
							backgroundColor='transparent'
						></Box>
					);
				}

				// Extract the computed values for each event
				const { isStart, isEnd, isInRange, lineBGColor } = info;

				return (
					<Box
						key={index}
						width='full'
						position='relative'
						height='2px'
						marginBottom='2px'
						backgroundColor={isInRange ? lineBGColor : 'transparent'}
					>
						{isStart && (
							<Box
								width='full'
								height='2px'
								backgroundColor={lineBGColor}
								position='absolute'
								top='50%'
								left='0'
								transform='translateY(-50%)'
							/>
						)}
						{isEnd && (
							<Box
								width='full'
								height='2px'
								backgroundColor={lineBGColor}
								position='absolute'
								top='50%'
								right='0'
								transform='translateY(-50%)'
							/>
						)}
						{isStart && (
							<Box
								width='6px'
								height='6px'
								borderRadius='50%'
								backgroundColor={lineBGColor}
								position='absolute'
								left='0'
								top='50%'
								transform='translateY(-50%)'
								zIndex={1}
							/>
						)}
						{isEnd && (
							<Box
								width='6px'
								height='6px'
								borderRadius='50%'
								backgroundColor={lineBGColor}
								position='absolute'
								right='0'
								top='50%'
								transform='translateY(-50%)'
								zIndex={1}
							/>
						)}
					</Box>
				);
			})}
		</React.Fragment>
	);
};
